import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { faClock, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Node as ProseNode } from "prosemirror-model";

import { DUE_DATE_VIEW, REASSIGN_VIEW } from "~/actionItems/constants";
import { DueDate } from "~/common";
import Checkbox from "~/common/NoteCheckbox";
import ViewportContext from "~/common/ViewportContext";
import BlockTextEditor, { ChangeAction } from "~/lib/prose/BlockTextEditor";
import { isTeams } from "~/lib/utils";
import AssignableDropdown, {
	type AssignableDropdownMenuSections,
	getDueDateItem,
	getLazyReassignMenuItem,
} from "~/notes/AssignableDropdown";
import { Button } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { IconButton } from "~/ui/legacyFellowIndex";

import ActionItemAssignees from "./ActionItemAssignees";
import { NoteBlockStatus, StatusCondition } from "./enum";

import itemStyles from "./ActionItem.module.css";
import actionsStyles from "./ActionItemActions.module.css";
import styles from "./NewActionItem.module.css";

function getAnimatedParent(from: HTMLElement): HTMLElement | null {
	const el = from.closest("data-action-items-animated-layout");
	if (el instanceof HTMLElement) return el;
	return null;
}

interface NewActionItemProps {
	user: { id: string; fullName: string; image?: string; email?: string };
	onContentChanged(newContent: string): void;
	dueDate?: string;
	assignees?: UserAttrs[];
	statusCondition: StatusCondition;
	onDueDateChanged(newDueDate: string): void;
	onAssigneesChanged(newAssignees?: UserAttrs[] | undefined): void;
	onStatusConditionChanged(newStatusCondition: StatusCondition): void;
	hasAccessToDueDates: boolean;
	hasAccessToMultipleAssignees: boolean;
	allowCompleteByAny: boolean;
	onSubmit(content: string): void;
	textVersion?: number;
}

export default function NewActionItem({
	user,
	onContentChanged,
	dueDate,
	assignees,
	statusCondition,
	onDueDateChanged,
	onAssigneesChanged,
	onStatusConditionChanged,
	hasAccessToDueDates,
	hasAccessToMultipleAssignees,
	allowCompleteByAny,
	onSubmit,
	textVersion,
}: NewActionItemProps) {
	const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false);
	const [subview, setSubview] = useState<string>(DUE_DATE_VIEW);
	const [portal, setPortal] = useState<HTMLElement>();
	const { isTablet } = useContext(ViewportContext);

	const actionsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isDropdownOpened) return;
		const el = document.createElement("div");

		let animatedParent: HTMLElement | null = null;
		if (isTablet && actionsRef.current) {
			actionsRef.current.appendChild(el);
			animatedParent = getAnimatedParent(actionsRef.current);
		} else {
			document.body.appendChild(el);
		}

		// FIXME: This is a disgusting hack to prevent the assignable dropdown from
		// being covered by other elements. Since we're animating the layout, there
		// is a transform: translate3d() applied to the item we're rendered in
		// here. Since the portal is inside the action item on non-mobile
		// platforms, the dropdown is affected and ends up somehow beneath the
		// following action items.
		//
		// To fix this, we set transform to none on the element when the menu is
		// open, and then stop overriding transform when it's closed.
		if (animatedParent) {
			animatedParent.classList.add(actionsStyles.forceNoTransform);
		}
		setPortal(el);
		return () => {
			// Part of the aforementioned hack:
			if (animatedParent) animatedParent.classList.remove(actionsStyles.forceNoTransform);

			el.remove();
			setPortal(undefined);
		};
	}, [isDropdownOpened, actionsRef, isTablet]);

	const onOpenDropdown = useCallback(() => setIsDropdownOpened(true), []);
	const onCloseDropdown = useCallback(() => setIsDropdownOpened(false), []);

	const onOpenDueDate = useCallback(() => {
		setSubview(DUE_DATE_VIEW);
		onOpenDropdown();
	}, [onOpenDropdown]);

	const onOpenAssignee = useCallback(() => {
		setSubview(REASSIGN_VIEW);
		onOpenDropdown();
	}, [onOpenDropdown]);

	const onContentSubmitted = useCallback(
		(prevDoc: ProseNode, doc: ProseNode, action: ChangeAction) => {
			const content = doc.toJSON().content;
			if (!content) return;
			onContentChanged(content);
			if (action === ChangeAction.enter && content) {
				onSubmit(content);
			}
		},
		[onContentChanged, onSubmit],
	);

	const generateDueDateSection = useCallback(
		(close: () => void) => {
			return getDueDateItem({
				hasAccessToDuedates: hasAccessToDueDates,
				setBlocksAttrs: (updates: { [key: string]: string }) => {
					onDueDateChanged(updates.dueDate);
					onCloseDropdown();
				},
				dueDates: [dueDate || null],
				close: () => {
					close();
					onCloseDropdown();
				},
				hasAccessToOriginalNote: true,
				originalBlockIds: [],
				blockIdsToNoteIds: {},
				isTeams,
			});
		},
		[hasAccessToDueDates, onCloseDropdown, dueDate, onDueDateChanged],
	);

	const reassignSection = useMemo(() => {
		return getLazyReassignMenuItem({
			updateItemAttrs: (updates: ProseNode["attrs"]) => {
				if (updates.assignees) onAssigneesChanged(updates.assignees);
				if (updates.statusCondition) onStatusConditionChanged(updates.statusCondition);
			},
			// @ts-ignore
			currentAssignees: assignees?.map(a => {
				return { ...a, isExternalInNote: a.isExternal }; // TODO: get if external in note
			}),
			stream: {
				displayName: "",
				isTodoStream: true,
				isExternal: false,
				type: "NoteStream",
			},
			// @ts-ignore
			item: {
				done: false,
				assignees:
					assignees?.map(a => {
						return { id: a.id, status: NoteBlockStatus.NOT_DONE };
					}) ?? [],
				statusCondition: statusCondition || StatusCondition.SINGLE,
			},
			user,
			showHeading: true,
			hasAccessToOriginalNote: true,
			isTeams,
			noModal: true,
			getAllAssigneeData: true,
			disableExternalDashes: true,
			hasAccessToMultipleAssignees,
			allowCompleteByAny,
			done: false,
		});
	}, [
		assignees,
		user,
		onAssigneesChanged,
		onStatusConditionChanged,
		hasAccessToMultipleAssignees,
		allowCompleteByAny,
		statusCondition,
	]);

	const menuSections = useCallback(
		(close: () => void) => {
			const sections: AssignableDropdownMenuSections = [];
			const dueDateSection = generateDueDateSection(close);
			if (dueDateSection) {
				sections.push({ children: [dueDateSection] });
			}
			if (reassignSection) {
				sections.push({ children: [reassignSection] });
			}
			return sections;
		},
		[generateDueDateSection, reassignSection],
	);

	const assignments = useMemo(
		() =>
			assignees?.map(a => {
				// TODO: how to make these types happy without assigning empty strings
				return {
					status: NoteBlockStatus.NOT_DONE,
					assignee: {
						id: a.id,
						fullName: a.fullName ?? "",
						image: a.image?.toString() ?? "",
						isGuest: !!a.isGuest,
						isExternal: !!a.isExternal,
						email: a.email ?? "",
						isActive: !!a.isActive,
					},
				};
			}) ?? null,
		[assignees],
	);

	return (
		<div className={itemStyles.actionItem} draggable={false} data-is-action-item>
			<Checkbox type="action-item" state="unchecked" checkboxSize="large" className={itemStyles.checkbox} readOnly />
			<div className={itemStyles.actionItemInner}>
				<div className={itemStyles.actionItemContent}>
					<BlockTextEditor
						deleteOut
						text={""}
						entityMap={{}}
						entityRanges={[]}
						participants={[]}
						enabled
						onSubmit={onContentSubmitted}
						className={cx(itemStyles.blockEditor, itemStyles.editing, styles.blockEditor)}
						version={textVersion}
						hideControls
						keepOpened
						submitOnEmptyBackspace={false}
					/>
					<div className={actionsStyles.actions} ref={actionsRef} data-action-item-actions>
						<div className={actionsStyles.actionsTrigger}>
							<span className={cx(actionsStyles.dueDateContainer, styles.dueDate)}>
								{dueDate ? (
									<DueDate
										as={Button}
										done={false}
										date={dueDate}
										className={cx("time", actionsStyles.duedate)}
										onClick={onOpenDueDate}
									/>
								) : (
									<IconButton
										icon={faClock}
										className={cx(actionsStyles.actionsClock, styles.actionsClock)}
										onClick={onOpenDueDate}
									/>
								)}
							</span>
							<Button size="wide" className={actionsStyles.actionsAssignee} onClick={onOpenAssignee}>
								{assignments && assignments.length > 0 ? (
									<ActionItemAssignees assignments={assignments} user={user} />
								) : (
									<FontAwesomeIcon icon={faUser} />
								)}
							</Button>
						</div>
					</div>
				</div>
			</div>
			{isDropdownOpened && portal && (
				<AssignableDropdown
					initialSubView={subview}
					className={actionsStyles.itemDropdown}
					anchor={actionsRef.current || undefined}
					portal={portal}
					onClose={onCloseDropdown}
					menuSections={menuSections}
				/>
			)}
		</div>
	);
}
